import React, { useState } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Paper
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

/**
 * Inline CAMO QM completion acknowledgment form (used in review tab and dialog).
 * Checklist completion is performed by MRO Engineer (not CAMO QM).
 */
const CamoQmCompletionAcknowledgmentForm = ({ workOrder, onSuccess, onCancel, embedded = false }) => {
  const [decision, setDecision] = useState('ACKNOWLEDGE');
  const [notes, setNotes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const checklistCount = Array.isArray(workOrder?.ampChecks) ? workOrder.ampChecks.length : 0;
  const checklistCompleted = checklistCount
    ? workOrder.ampChecks.filter((c) => c.status === 'COMPLETED').length
    : 0;

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const cleanedRemarks = remarks.trim();
      if (decision === 'REJECT' && !cleanedRemarks) {
        setError('Rejection remarks are required');
        setLoading(false);
        return;
      }

      await axios.post(`/api/maintenance/${workOrder._id}/camo-qm/acknowledge-completion`, {
        decision,
        notes: notes.trim() || undefined,
        remarks: cleanedRemarks || undefined
      });

      showToast(
        decision === 'REJECT' ? 'Completion rejected successfully' : 'Completion acknowledged successfully',
        'success'
      );
      onSuccess?.();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to acknowledge completion';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const Wrapper = embedded ? Paper : Box;

  return (
    <Wrapper variant={embedded ? 'outlined' : undefined} sx={{ p: embedded ? 2 : 0 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        CAMO QM Completion Acknowledgment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        MRO Engineer completes the checklist and work. Review the work order above, then acknowledge or reject completion.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Checklist items are completed by the MRO Engineer (with Tech and AME signatures) before engineer WO completion.
        MRO QM acknowledgment is required before CAMO QM review.
        {checklistCount > 0
          ? ` Checklist progress: ${checklistCompleted}/${checklistCount} completed by MRO.`
          : ''}
      </Alert>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Decision
        </Typography>
        <ToggleButtonGroup
          value={decision}
          exclusive
          onChange={(_, v) => v && setDecision(v)}
          fullWidth
          size="small"
        >
          <ToggleButton value="ACKNOWLEDGE">Acknowledge Completion</ToggleButton>
          <ToggleButton value="REJECT" color="error">Reject Completion</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Acknowledgment Notes (Optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add any notes or comments about the completion acknowledgment..."
        sx={{ mb: 2 }}
      />

      {decision === 'REJECT' && (
        <TextField
          fullWidth
          required
          multiline
          rows={3}
          label="Rejection Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Explain why this completion is rejected and what must be corrected..."
          sx={{ mb: 2 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={decision === 'REJECT' ? 'error' : 'success'}
          disabled={loading || (decision === 'REJECT' && !remarks.trim())}
        >
          {loading ? 'Submitting...' : (decision === 'REJECT' ? 'Reject Completion' : 'Acknowledge Completion')}
        </Button>
      </Box>
    </Wrapper>
  );
};

export default CamoQmCompletionAcknowledgmentForm;
